import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Store, Eye, Lock, Unlock, Trash2, Wallet, Percent, TrendingUp, Users, Package, BadgeCheck,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatPrice, formatDate } from "@/lib/format";
import { AdminModal, Field, Pill, inputCls } from "@/pages/admin/ui";

const STATUS = {
  active: { label: "🟢 Actif", tone: "green" },
  trial: { label: "🎁 Essai", tone: "blue" },
  expired: { label: "⚠️ Expiré", tone: "amber" },
  blocked: { label: "🔒 Bloqué", tone: "rose" },
};
const FILTERS = [["", "Tous"], ["trial", "En essai"], ["active", "Abonnés"], ["expired", "Expirés"], ["blocked", "Bloqués"]];

export default function AdminVendors() {
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [filter, setFilter] = useState("");
  const [plans, setPlans] = useState([]);
  const [detail, setDetail] = useState(null);
  const [sub, setSub] = useState({ plan_id: "", days: "" });
  const [commission, setCommission] = useState("");
  const [sanctions, setSanctions] = useState([]);
  const [sanction, setSanction] = useState({ action: "warning", reason: "", days: "" });
  const [sanctionBusy, setSanctionBusy] = useState(false);

  const SANCTIONS = [
    ["warning", "⚠️ Avertissement", "Un simple avertissement enregistré dans l'historique"],
    ["suspend", "⏸ Suspension temporaire", "Le compte est suspendu pendant X jours"],
    ["block", "🔒 Blocage temporaire", "La boutique est bloquée pendant X jours"],
    ["suspend_permanent", "🚫 Suspension définitive", "Suspension sans date de fin (levable)"],
    ["ban", "⛔ Bannissement définitif", "Bannissement total et définitif du site"],
    ["lift", "✅ Lever la sanction", "Réactiver le compte et lever toutes les sanctions"],
  ];

  const load = () => {
    api.get(`/admin/vendors${filter ? `?status=${filter}` : ""}`).then(({ data }) => setVendors(data)).catch(() => setVendors([]));
    api.get("/admin/vendors/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.get("/admin/vendor-plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/vendors/${id}`);
      setDetail(data); setSub({ plan_id: data.plan_id || (plans[0]?.id || ""), days: "" });
      setCommission(data.commission_percent ?? "");
      api.get(`/admin/vendors/${id}/sanctions`).then(({ data: s }) => setSanctions(s)).catch(() => setSanctions([]));
    }
    catch (e) { toast.error(apiError(e)); }
  };

  const applySanction = async () => {
    if (["suspend", "block"].includes(sanction.action) && !parseInt(sanction.days || 0, 10)) {
      toast.error("Indiquez la durée en jours"); return;
    }
    if (sanction.action === "ban" && !window.confirm("⛔ Bannir DÉFINITIVEMENT ce revendeur du site ?")) return;
    setSanctionBusy(true);
    try {
      await api.post(`/admin/vendors/${detail.id}/sanction`, {
        action: sanction.action, reason: sanction.reason, days: parseInt(sanction.days || 0, 10),
      });
      toast.success("Sanction appliquée et enregistrée dans l'historique du revendeur");
      setSanction({ action: "warning", reason: "", days: "" });
      openDetail(detail.id); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSanctionBusy(false); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); if (detail) openDetail(detail.id); } catch (e) { toast.error(apiError(e)); } };
  const block = (v) => act(() => api.post(`/admin/vendors/${v.id}/block`), "Revendeur bloqué");
  const unblock = (v) => act(() => api.post(`/admin/vendors/${v.id}/unblock`), "Revendeur débloqué");
  const del = (v) => { if (window.confirm(`Voulez-vous vraiment supprimer le compte revendeur « ${v.shop_name} » ?`)) act(() => api.delete(`/admin/vendors/${v.id}`), "Revendeur supprimé"); };
  const activateSub = () => act(() => api.put(`/admin/vendors/${detail.id}/subscription`, { action: "activate", plan_id: sub.plan_id, days: parseInt(sub.days || 0, 10) }), "Abonnement activé");
  const saveCommission = () => act(() => api.put(`/admin/vendors/${detail.id}/subscription`, { action: "set_commission", commission_percent: commission === "" ? null : parseInt(commission, 10) }), "Commission mise à jour");

  const STAT_CARDS = stats ? [
    { label: "Revendeurs", value: stats.total, icon: Store },
    { label: "Actifs", value: stats.active, icon: BadgeCheck },
    { label: "En essai", value: stats.trial, icon: Users },
    { label: "Abonnés", value: stats.subscribed, icon: Wallet },
    { label: "Expirés", value: stats.expired, icon: Percent },
    { label: "CA global", value: formatPrice(stats.global_revenue), icon: TrendingUp },
    { label: "Ventes", value: stats.total_sales, icon: TrendingUp },
    { label: "Produits vendeurs", value: stats.vendor_products, icon: Package },
  ] : [];

  return (
    <div className="space-y-5" data-testid="admin-vendors">
      <h1 className="font-display text-2xl font-extrabold text-ink">Revendeurs</h1>

      {stats && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {STAT_CARDS.map(({ label, value, icon: Icon }) => (
            <div key={label} className="rounded-2xl border border-line bg-white p-4">
              <div className="flex items-center justify-between"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</span><Icon className="h-4 w-4 text-brand" /></div>
              <p className="mt-2 font-display text-lg font-extrabold text-ink">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`vfilter-${v || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold ${filter === v ? "bg-ink text-white" : "border border-line bg-white text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {vendors === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : vendors.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center text-sm text-slate-500">Aucun revendeur.</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-line bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-slate-400">
              <th className="p-4">Boutique</th><th className="p-4">Abonnement</th><th className="p-4">Statut</th><th className="p-4">Produits</th><th className="p-4">CA</th><th className="p-4 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-line">
              {vendors.map((v) => {
                const st = STATUS[v.status] || {};
                return (
                  <tr key={v.id} data-testid={`admin-vendor-${v.id}`}>
                    <td className="p-4"><span className="font-bold text-ink">{v.shop_name}</span><br /><span className="text-xs text-slate-400">{v.email}</span></td>
                    <td className="p-4 text-ink">{v.plan_name || "—"}</td>
                    <td className="p-4"><Pill tone={st.tone || "slate"}>{st.label || v.status}</Pill></td>
                    <td className="p-4">{v.products}</td>
                    <td className="p-4 font-bold text-ink">{formatPrice(v.revenue)}</td>
                    <td className="p-4">
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => openDetail(v.id)} data-testid={`vendor-view-${v.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-slate-500 hover:text-brand"><Eye className="h-4 w-4" /></button>
                        {v.is_blocked
                          ? <button onClick={() => unblock(v)} data-testid={`vendor-unblock-${v.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-emerald-600 hover:bg-emerald-50"><Unlock className="h-4 w-4" /></button>
                          : <button onClick={() => block(v)} data-testid={`vendor-block-${v.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-amber-600 hover:bg-amber-50"><Lock className="h-4 w-4" /></button>}
                        <button onClick={() => del(v)} data-testid={`vendor-delete-${v.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AdminModal open={!!detail} onOpenChange={(o) => !o && setDetail(null)} title={detail?.shop_name || ""} wide>
        {detail && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Statut", (STATUS[detail.status] || {}).label], ["Abonnement", detail.plan_name || "—"],
                ["Produits", detail.products], ["Formations", detail.formations],
                ["Commandes", detail.orders], ["CA", formatPrice(detail.revenue)],
                ["Commission", `${detail.commission_percent}%`], ["Expire", detail.subscription_expires_at ? formatDate(detail.subscription_expires_at) : "—"]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-line bg-surface p-3"><p className="text-[10px] font-bold uppercase text-slate-400">{l}</p><p className="mt-1 text-sm font-bold text-ink">{v}</p></div>
              ))}
            </div>

            <div className="rounded-xl border border-line p-4">
              <p className="mb-3 font-display text-sm font-bold text-ink">💳 Gérer l'abonnement</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Plan"><select value={sub.plan_id} onChange={(e) => setSub((s) => ({ ...s, plan_id: e.target.value }))} className={inputCls}>{plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${formatPrice(p.price)}`}</option>)}</select></Field>
                <Field label="Durée (jours, vide = défaut)"><input value={sub.days} onChange={(e) => setSub((s) => ({ ...s, days: e.target.value }))} className={inputCls} placeholder="30" /></Field>
                <div className="flex items-end"><button onClick={activateSub} data-testid="vendor-activate-sub" className="w-full rounded-lg bg-brand py-2.5 text-sm font-bold text-white">Activer l'abonnement</button></div>
              </div>
            </div>

            <div className="rounded-xl border border-line p-4">
              <p className="mb-3 font-display text-sm font-bold text-ink">💰 Commission personnalisée</p>
              <div className="flex items-end gap-3">
                <Field label="% (vide = commission globale)" className="flex-1"><input value={commission} onChange={(e) => setCommission(e.target.value)} className={inputCls} placeholder="ex: 10" /></Field>
                <button onClick={saveCommission} className="rounded-lg border border-line px-5 py-2.5 text-sm font-bold text-ink hover:border-brand">Enregistrer</button>
              </div>
            </div>

            <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4" data-testid="vendor-sanctions">
              <p className="mb-3 font-display text-sm font-bold text-ink">⚖️ Sanctions & mesures disciplinaires</p>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Mesure">
                  <select value={sanction.action} onChange={(e) => setSanction((s) => ({ ...s, action: e.target.value }))} className={inputCls} data-testid="sanction-action">
                    {SANCTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                {["suspend", "block"].includes(sanction.action) && (
                  <Field label="Durée (jours)">
                    <input type="number" min="1" value={sanction.days} onChange={(e) => setSanction((s) => ({ ...s, days: e.target.value }))} className={inputCls} data-testid="sanction-days" placeholder="7" />
                  </Field>
                )}
                <Field label="Motif (visible par le revendeur)">
                  <input value={sanction.reason} onChange={(e) => setSanction((s) => ({ ...s, reason: e.target.value }))} className={inputCls} data-testid="sanction-reason" placeholder="Non-respect des règles..." />
                </Field>
              </div>
              <p className="mb-3 mt-1 text-[11px] text-slate-500">{(SANCTIONS.find(([v]) => v === sanction.action) || [])[2]}</p>
              <button onClick={applySanction} disabled={sanctionBusy} data-testid="sanction-apply"
                className="rounded-full bg-rose-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60">
                {sanctionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Appliquer la mesure"}
              </button>
              {sanctions.length > 0 && (
                <div className="mt-4 space-y-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Historique des sanctions</p>
                  {sanctions.map((s) => (
                    <div key={s.id} className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2 text-xs" data-testid={`sanction-${s.id}`}>
                      <span><span className="font-bold text-ink">{s.action_label}</span>{s.days ? ` · ${s.days}j` : ""}{s.reason ? ` — ${s.reason}` : ""}</span>
                      <span className="text-slate-400">{formatDate(s.created_at)} · {s.by}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {detail.is_blocked
                ? <button onClick={() => unblock(detail)} data-testid="vendor-unblock" className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white">🔓 Débloquer</button>
                : <button onClick={() => block(detail)} data-testid="vendor-block" className="rounded-full bg-amber-600 px-5 py-2.5 text-sm font-bold text-white">🔒 Bloquer</button>}
              <a href={`/boutique-vendeur/${detail.shop_slug}`} target="_blank" rel="noreferrer" data-testid="vendor-view-shop" className="rounded-full border border-line px-5 py-2.5 text-sm font-bold text-ink hover:border-brand">Voir la boutique</a>
              <button onClick={() => del(detail)} data-testid="vendor-delete" className="rounded-full border border-rose-200 px-5 py-2.5 text-sm font-bold text-rose-600 hover:bg-rose-50">🗑 Supprimer</button>
            </div>

            {detail.activities?.length > 0 && (
              <div>
                <p className="mb-2 font-display text-sm font-bold text-ink">Activités récentes</p>
                <div className="space-y-1.5">
                  {detail.activities.slice(0, 8).map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-lg bg-surface px-3 py-2 text-xs">
                      <span className="text-slate-600">{a.message}</span><span className="text-slate-400">{formatDate(a.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </AdminModal>
    </div>
  );
}
